// sanitizeForJson — cycle-breaking, JSON-safe deep copy for request
// bodies that are about to hit JSON.stringify.
//
// WHY: "TypeError: JSON.stringify cannot serialize cyclic structures"
// (WebKit wording) was killing AI recipe drafts whenever ANY object in
// the assembled payload carried a back-reference — and because the
// throw happens at the fetch/invoke boundary, the user saw the
// stringify error INSTEAD of a recipe, constantly, with no hint of
// which field was contaminated. supabase-js invoke() and our raw
// fetch() both stringify the body verbatim, so one cyclic node
// anywhere in pantry/context/conceptFrame/prefs sank the whole draft.
//
// This helper makes the boundary unconditionally safe AND
// self-diagnosing:
//   • cycles are broken (the back-referencing node is dropped) and
//     reported via onDrop(path, reason) so the console names the
//     exact payload path that carried the cycle — the next
//     occurrence identifies its own source,
//   • functions / symbols / undefined are dropped (stringify would
//     drop them anyway, but dropping them here keeps the cycle walk
//     out of closure scopes),
//   • DOM nodes and window are dropped (the classic smuggler is a
//     React SyntheticEvent or element ref captured into state —
//     event.target → element → ownerDocument → defaultView → window
//     is itself a cycle),
//   • Dates → ISO strings, Map → plain object, Set → array,
//     NaN/Infinity → null (stringify would emit null anyway),
//   • depth-capped as a runaway backstop.
//
// Shared-but-ACYCLIC references stay intact: cycle detection is
// ancestor-path based (add → recurse → remove), so the same object
// referenced from two sibling branches serializes in both places.

const MAX_DEPTH = 64;

export function sanitizeForJson(value, { onDrop = null } = {}) {
  const ancestors = new WeakSet();

  const walk = (v, path, depth) => {
    if (v === null) return null;
    const t = typeof v;
    if (t === "string" || t === "boolean") return v;
    if (t === "number") return Number.isFinite(v) ? v : null;
    if (t === "bigint") return v.toString();
    if (t === "function" || t === "symbol" || t === "undefined") return undefined;

    // Object territory from here.
    if (depth > MAX_DEPTH) {
      onDrop?.(path, "max-depth");
      return undefined;
    }
    if (v instanceof Date) {
      return Number.isNaN(v.getTime()) ? null : v.toISOString();
    }
    if (typeof window !== "undefined" && v === window) {
      onDrop?.(path, "window");
      return undefined;
    }
    if (typeof Node !== "undefined" && v instanceof Node) {
      onDrop?.(path, "dom-node");
      return undefined;
    }
    if (ancestors.has(v)) {
      onDrop?.(path, "cycle");
      return undefined;
    }
    // Objects with their own toJSON (e.g. dayjs-likes) — trust it,
    // then re-walk the result in case it still carries objects.
    if (typeof v.toJSON === "function" && !(v instanceof Date)) {
      try {
        return walk(v.toJSON(), `${path}.toJSON()`, depth + 1);
      } catch {
        onDrop?.(path, "toJSON-threw");
        return undefined;
      }
    }

    ancestors.add(v);
    try {
      if (Array.isArray(v)) {
        const out = new Array(v.length);
        for (let i = 0; i < v.length; i += 1) {
          const w = walk(v[i], `${path}[${i}]`, depth + 1);
          // JSON.stringify turns un-serializable array slots into
          // null; match that so array lengths are stable.
          out[i] = w === undefined ? null : w;
        }
        return out;
      }
      if (v instanceof Map) {
        const out = {};
        for (const [k, mv] of v.entries()) {
          const w = walk(mv, `${path}.${String(k)}`, depth + 1);
          if (w !== undefined) out[String(k)] = w;
        }
        return out;
      }
      if (v instanceof Set) {
        const out = [];
        let i = 0;
        for (const sv of v.values()) {
          const w = walk(sv, `${path}{${i}}`, depth + 1);
          if (w !== undefined) out.push(w);
          i += 1;
        }
        return out;
      }
      const out = {};
      for (const k of Object.keys(v)) {
        const w = walk(v[k], `${path}.${k}`, depth + 1);
        if (w !== undefined) out[k] = w;
      }
      return out;
    } finally {
      ancestors.delete(v);
    }
  };

  return walk(value, "$", 0);
}

// Convenience wrapper for request bodies: sanitize + warn once per
// dropped node with the payload path, tagged by call site.
export function sanitizeRequestBody(body, label = "request") {
  return sanitizeForJson(body, {
    onDrop: (path, reason) => {
      // eslint-disable-next-line no-console
      console.warn(
        `[${label}] payload sanitizer dropped a ${reason} at ${path} — ` +
        "the request proceeds without it. If this is a cycle, the path " +
        "above names the state that needs cleaning at its source.",
      );
    },
  });
}
